// ============================================================================
// KNOWN ROLES BOOT
// ============================================================================
// Hydrates the DB-driven role catalog into localStorage before the login
// pipeline calls resolveHighestPriorityRole. The catalog is a small array of
// { roleCode, roleName, priority, isSystemRole } fetched from
// GET /api/v1/RolePermissions/known-roles.
//
// Kept in its own file (rather than inside config/rolePermissions.ts) so it
// can import rolePermissionService without creating a circular boot-time
// import cycle: rolePermissionService → axios interceptors → auth helpers.
//
// Caller: login-interface.tsx invokes this before pickHighestPriorityRole.
// If it fails (network, backend down, endpoint not yet deployed), the
// resolver falls back to the four baseline role codes hardcoded in
// config/rolePermissions.ts — so the app stays functional for baseline
// roles even when the endpoint is unreachable.

import { rolePermissionService } from '../api/services/rolePermissionService';
import {
  setCachedKnownRoles,
  isKnownRolesCacheFresh,
} from '../config/rolePermissions';

/**
 * Ensures the known-roles catalog is present in localStorage. Uses the
 * existing cache when fresh (within KNOWN_ROLES_TTL_MS); otherwise fetches
 * from /api/v1/RolePermissions/known-roles and writes through.
 *
 * Returns `true` when the catalog is available (from cache or fresh fetch),
 * `false` when both the cache is stale AND the fetch failed.
 *
 * @param force when `true`, ignores the freshness check and always refetches.
 *              Callers that just mutated role_permissions should pass `true`.
 */
export async function hydrateKnownRoles(force = false): Promise<boolean> {
  if (!force && isKnownRolesCacheFresh()) {
    return true;
  }

  try {
    const response = await rolePermissionService.getKnownRoles();
    if (response.success && Array.isArray(response.data)) {
      setCachedKnownRoles(response.data);
      return true;
    }
    return false;
  } catch {
    // Non-fatal — resolveHighestPriorityRole falls back to the baseline
    // codes when the cache is missing. Login continues to work for the four
    // seeded roles (crs_system, crs_admin, crs_booking_agent, crs_user)
    // even without the endpoint.
    return false;
  }
}
